import { useQuery } from '@tanstack/react-query';
import type { Paginated } from '@barbeiropro/contracts';
import { api } from '@/lib/api';

export interface Asset {
  id: string;
  url: string;
  alt: string;
}

export interface Category {
  id: string;
  name: string;
  position: number;
}

export interface ServiceRow {
  id: string;
  name: string;
  description: string | null;
  categoryId: string | null;
  categoryName: string | null;
  durationMinutes: number;
  priceCents: number;
  active: boolean;
  publicVisible: boolean;
  imageAssetId: string | null;
  deletedAt: string | null;
  barbersCount: number;
}

export interface BarberRow {
  id: string;
  displayName: string;
  bio: string | null;
  specialties: string[];
  photoAssetId: string | null;
  branchId: string;
  chairId: string | null;
  chairName: string | null;
  color: string;
  monthlyGoalCents: number | null;
  active: boolean;
  publicVisible: boolean;
  deletedAt: string | null;
  serviceIds: string[];
}

export interface Chair {
  id: string;
  branchId: string;
  name: string;
  active: boolean;
}

export interface BarberServiceItem {
  serviceId: string;
  priceCentsOverride: number | null;
  durationMinutesOverride: number | null;
}

export interface Member {
  id: string;
  userId: string;
  name: string;
  email: string;
  roleId: string;
  roleName: string;
  roleKey: string | null;
  barberId: string | null;
  active: boolean;
}

export interface Role {
  id: string;
  key: string | null;
  name: string;
  description: string | null;
  permissions: string[];
}

export interface Invitation {
  id: string;
  email: string;
  roleName: string;
  expiresAt: string;
  acceptedAt: string | null;
  revokedAt: string | null;
  createdAt: string;
}

/** Todos os arquivos da barbearia; serve para resolver id -> URL de imagem. */
export const useAssets = () => useQuery({ queryKey: ['assets'], queryFn: () => api.get<Asset[]>('/assets'), staleTime: 60_000 });

export function useAssetUrl(id: string | null | undefined) {
  const { data } = useAssets();
  return id ? (data?.find((a) => a.id === id)?.url ?? null) : null;
}

export const useCategories = () => useQuery({ queryKey: ['catalog', 'categories'], queryFn: () => api.get<Category[]>('/service-categories'), staleTime: 60_000 });

export const useChairs = (branchId?: string) =>
  useQuery({ queryKey: ['org', 'chairs', branchId ?? 'all'], queryFn: () => api.get<Chair[]>('/org/chairs', { branchId }), staleTime: 60_000 });

export const useRoles = () => useQuery({ queryKey: ['org', 'roles'], queryFn: () => api.get<Role[]>('/org/roles'), staleTime: 60_000 });

/** Todos os serviços ativos (a API pagina em 100). */
export async function fetchAllServices(query: Record<string, string> = {}) {
  const out: ServiceRow[] = [];
  for (let page = 1; page <= 20; page++) {
    const r = await api.get<Paginated<ServiceRow>>('/services', { ...query, page, pageSize: 100, sort: 'name', order: 'asc' });
    out.push(...r.data);
    if (out.length >= r.total) break;
  }
  return out;
}

export const useAllServices = () => useQuery({ queryKey: ['catalog', 'services', 'all'], queryFn: () => fetchAllServices({ active: 'true' }), staleTime: 60_000 });
